import { Close } from "@mui/icons-material"
import { Accordion, AccordionDetails, AccordionSummary, Box, Checkbox, Divider, Drawer, IconButton, Stack } from "@mui/material"
import ButtonLarge1 from "components/elements/ButtonLarge1"
import ButtonLarge4 from "components/elements/ButtonLarge4"
import ContentNormal from "components/elements/ContentNormal"
import Headline5 from "components/elements/Headline5"
import CaretDown from "components/icons/CaretDown"
import { AppContext } from "contexts/AppContext"
import { useContext, useEffect, useState } from "react"
import { PriceFilter } from "./PriceFilter"
import { translate } from "utils/Translate"

export function ShopFilterDrawer(props){
  const {styleGuide} = useContext(AppContext)
  const [selectedManufacturers, setSelectedManufacturers] = useState(props.manufacturerFilterValue)
  const [selectedSizes, setSelectedSizes] = useState(props.sizeFilterValue)
  const [selectedPriceRange, setSelectedPriceRange] = useState([0, props.maxPriceValue])

  const onApplyClick = () => {
    props.onApply({
      selectedManufacturers: selectedManufacturers,
      selectedSizes: selectedSizes,
      selectedPriceRange: selectedPriceRange,
    })
    props.onClose()
  }

  const onResetClick = () => {
    props.onApply({
      selectedManufacturers: [],
      selectedSizes: [],
      selectedPriceRange: [0, props.maxPriceValue],
    })
    props.onClose()
  }

  useEffect(() => {
    setSelectedManufacturers(props.manufacturerFilterValue)
  }, [props.manufacturerFilterValue])

  useEffect(() => {
    setSelectedSizes(props.sizeFilterValue)
  }, [props.sizeFilterValue])

  useEffect(() => {
    setSelectedPriceRange(props.priceFilterValue)
  }, [props.priceFilterValue])

  return (
    <Drawer
      open={props.isOpen}
      onClose={props.onClose}
      sx={{ zIndex: (theme) => theme.zIndex.appBar - 1 }}
    >
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 3, padding: 3, width: '100vw',}}>
        <Box sx={{display: 'flex', justifyContent: 'space-between', gap: 2, paddingTop: 14}}>
          <Headline5
            translationKey="_SHOP_FILTER_DRAWER_HEADLINE_"
            fallbackText="Filtre"
          />

          <IconButton
            onClick={props.onClose}
            sx={{padding: 0, marginRight: -0.5}}
          >
            <Close />
          </IconButton>
        </Box>

        <Stack 
          divider={<Box sx={{borderTop: `1px solid ${styleGuide.color4}`}} />}
          spacing={1}
          sx={{
            borderTop: `1px solid ${styleGuide.color4}`,
            borderBottom: `1px solid ${styleGuide.color4}`,
            paddingY: 1,
          }}
        >
          {!!props.brandOptions.length &&
            <DrawerFilterSelect
              title={translate('_MANUFACTURER_FILTER_LABEL_', 'Brands')}
              options={props.brandOptions}
              selectedValues={selectedManufacturers}
              toggleOption={(optionValue) => setSelectedManufacturers((current) =>
                current.includes(optionValue)
                  ? current.filter((v) => v !== optionValue)
                  : [...current, optionValue]
              )}
            />
          }

          {!!props.sizeOptions.length &&
            <DrawerFilterSelect
              title={translate('_SIZE_FILTER_LABEL_', 'Størrelse')}
              options={props.sizeOptions}
              selectedValues={selectedSizes}
              toggleOption={(optionValue) => setSelectedSizes((current) =>
                current.includes(optionValue)
                  ? current.filter((v) => v !== optionValue)
                  : [...current, optionValue]
              )}
            />
          }

          <DrawerPriceFilter
            title={translate('_PRICE_FILTER_LABEL_', 'Pris')}
            maxValue={props.maxPriceValue}
            draftValue={selectedPriceRange}
            setDraftValue={setSelectedPriceRange}
          />
        </Stack>

        <Box sx={{display: 'flex', gap: 1}}>
          <ButtonLarge1
            onClick={onApplyClick}
            translationKey="_SHOP_APPLY_FILTERS_"
            fallbackText="Vis resultater"
            showIcon={false}
          />

          <ButtonLarge4
            onClick={onResetClick}
            translationKey="_SHOP_RESET_FILTERS_"
            fallbackText="Nulstil"
            showIcon={false}
          />
        </Box>
      </Box>
    </Drawer>
  )
}

function DrawerFilter(props){
  const {title, children} = props

  return (
    <Accordion
      sx={{
        boxShadow: 'none',
        '&.Mui-expanded': {
          margin: 0
        },
        '&:before': {
          display: 'none',
        },
      }}
    >
      <AccordionSummary expandIcon={<CaretDown />} sx={{padding: 0, paddingRight: 0.1}}  >
        <ContentNormal>
          {title}
        </ContentNormal>
      </AccordionSummary>
      <AccordionDetails sx={{padding: 0}}>
        {children}
      </AccordionDetails>
    </Accordion>
  )
}

function DrawerFilterSelect(props){
  const {title, options, toggleOption, selectedValues} = props
  const {styleGuide} = useContext(AppContext)

  return (
    <DrawerFilter title={title}>
      <Box sx={{ backgroundColor: styleGuide.color3, paddingY: 3, paddingX: 1, borderRadius: 3 }}>
      <Box sx={{ maxHeight: 250, overflowY: 'auto', paddingX: 2 }}>
        <Stack divider={<Divider />} >
          {options.map(option => {
            return (
              <Box key={option.label} display={'flex'} justifyContent={'space-between'} alignItems={'center'} paddingY={1} borderBottom={`1px solid ${styleGuide.color3}`}>
                <ContentNormal key={option.value}>
                  {option.label}
                </ContentNormal>
                <Checkbox
                  type="checkbox"
                  checked={selectedValues.includes(option.value)}
                  onChange={() => toggleOption(option.value)}
                  style={{ color: selectedValues.includes(option.value) ? styleGuide.color16 : '' }}
                />
              </Box>
            )
          })}
        </Stack>
      </Box>
      </Box>
    </DrawerFilter>
  )
}

function DrawerPriceFilter(props){
  const {title, maxValue, draftValue, setDraftValue} = props
  const {styleGuide} = useContext(AppContext)
  const [withinMyPointsChecked, setWithinMyPointsChecked] = useState(false)

  useEffect(() => {
    setDraftValue(prev => {
      if (prev[0] > maxValue) {
        return [maxValue, maxValue]
      } else if (prev[1] > maxValue) {
        return [0, maxValue]
      }
      return prev
    });
  }, [maxValue])

  return (
    <DrawerFilter title={title}>
      <Box sx={{ borderRadius: 3 }}>
        <PriceFilter
          maxValue={maxValue}
          draftValue={draftValue}
          setDraftValue={setDraftValue}
          withinMyPointsChecked={withinMyPointsChecked}
          setWithinMyPointsChecked={setWithinMyPointsChecked}
        />
      </Box>
    </DrawerFilter>
  )
}